<template>
  <div>
    <section class="content-group -bg-01 -center -no-spacer -vs-medium -column">
      <div class="group -flex -between -narrow -vs-small -vs-bottom -vs-collapse-bottom">
        <h1 id="spa-title" class="heading -primary">Dietary Approach</h1>
        <div class="spa-heading-action">
          <router-link to="/my/settings/energy">Back</router-link>
        </div>
      </div>
      <div class="group -narrow -vs-small -vs-collapse-top">
        <h2 class="heading -tertiary">My approach to food best aligns with:</h2>
      </div>

      <!-- mindful -->
      <div
        class="preference-card group -flex -between -narrow -vs-medium -vs-center -hs-medium -bg-08 -only-rounder -stack-space-double"
        :class="{
          '-mindful': !dietaryMethod || dietaryMethod === 'mindful',
          '-deselected': dietaryMethod && dietaryMethod !== 'mindful',
        }"
        role="button"
        tabindex="0"
        @click="selectMethod('mindful')"
        @keyup.enter="selectMethod('mindful')"
      >
        <div class="selection-text">
          <h2 class="heading -secondary">
            <span class="pre-heading -caps">Dietary Approach</span>
            Mindful Eating
          </h2>
          <p>
            I want to focus on how I feel, enjoy healthy foods when I’m hungry, and be mindful about
            when I’m full. I do not want to see calories and macronutrients in meal plans.
          </p>
        </div>
        <div
          class="selection-icon"
          :class="{ '-unselected': dietaryMethod && dietaryMethod !== 'mindful' }"
        >
          <i v-if="dietaryMethod === 'mindful'" class="material-symbols-outlined">check_circle</i>
          <i v-else class="material-symbols-outlined">radio_button_unchecked</i>
        </div>
      </div>
      <!-- End Mindful -->

      <!-- Estimates -->
      <div
        class="preference-card group -flex -between -narrow -vs-medium -vs-center -hs-medium -bg-08 -only-rounder -stack-space-double"
        :class="{
          '-estimates': !dietaryMethod || dietaryMethod === 'estimate',
          '-deselected': dietaryMethod && dietaryMethod !== 'estimate',
        }"
        role="button"
        tabindex="0"
        @click="selectMethod('estimate')"
        @keyup.enter="selectMethod('estimate')"
      >
        <div class="selection-text">
          <h2 class="heading -secondary">
            <span class="pre-heading -caps">Dietary Approach</span>
            Nutrition Estimates
          </h2>
          <p>
            I want to use data to help me estimate and track my nutrition and activity balance. I
            want to see calories and macronutrients in meal plans.
          </p>
        </div>
        <div
          class="selection-icon"
          :class="{ '-unselected': dietaryMethod && dietaryMethod !== 'estimate' }"
        >
          <i v-if="dietaryMethod === 'estimate'" class="material-symbols-outlined">check_circle</i>
          <i v-else class="material-symbols-outlined">radio_button_unchecked</i>
        </div>
      </div>
      <!-- End Estimates -->

      <!-- Page Action -->
      <div class="spa-action">
        <submit-button
          v-if="dietaryMethod"
          :submitting="saving"
          label="Save &amp; Continue"
          submitting-label="Processing…"
          classes="-main"
          @submit-action="saveDietaryMethod"
        />
        <button v-else class="btn -main -inactive" disabled="disabled">Choose an Option</button>
      </div>
      <!-- End Page Action -->
    </section>

    <more-info-section title="How does my choice make a difference?">
      <p>
        The most obvious differences between Mindful Eating and Nutrition Estimates are the
        information you see on recipes within Meal Plans, and how you make changes to portion
        amounts to fit your energy needs. Simply put, the Mindful Eating option does not display
        calorie and macronutrient information by default, but the Nutrition Estimates path does.
        These may seem like small differences, but they can drastically change your mindset toward
        eating.
      </p>
    </more-info-section>
  </div>
</template>

<script>
import { defineComponent } from 'vue'
import SubmitButton from '@/components/buttons/submit-button.vue'
import MoreInfoSection from './more-info-section.vue'

/**
 * Step 2 - Dietary Method Selection
 *
 * Initial state passed in for dietaryMethod, then self managed.
 */
export default defineComponent({
  name: 'DietaryStep',
  components: {
    MoreInfoSection,
    SubmitButton,
  },
  props: {
    initialDietaryMethod: {
      type: String,
      default: '',
    },

    // loading state from parent
    saving: {
      type: Boolean,
      default: false,
    },
  },
  emits: ['save-action'],
  data() {
    return {
      // initialize from prop, but then self manage
      dietaryMethod: this.initialDietaryMethod,
    }
  },

  computed: {},

  methods: {
    selectMethod(val) {
      this.dietaryMethod = val
    },

    // saves snake_case to make backend easier
    saveDietaryMethod() {
      const formData = {
        dietary_method: this.dietaryMethod,
      }

      // go to step 3
      const nextRoute = {
        path: '/my/settings/energy/calorie',
        query: {
          success: 'dietary',
        },
      }

      this.$emit('save-action', { formData, nextRoute })
    },
  },
})
</script>
